// Only the CRM app itself is skipped — upsales.com (the marketing site) is a
// normal company website and stays matchable.
const SKIPPED_DOMAINS = [
	'power.upsales.com',
	'google.com',
	'google.se',
	'bing.com',
	'duckduckgo.com',
	'office.com',
	'live.com',
	'outlook.com'
];

// Returns the normalized domain when the page plausibly belongs to a company
// website, or null when matching would be pointless (internal pages, search
// engines, webmail, Upsales itself).
export const matchablePageDomain = (url: string): string | null => {
	if (!url || !/^https?:\/\//i.test(url)) return null;
	const domain = url
		.replace(/^https?:\/\//i, '')
		.replace(/^www\./i, '')
		.replace(/[/?#].*$/, '')
		.replace(/:\d+$/, '')
		.toLowerCase();
	if (!domain.includes('.')) return null;
	if (domain === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(domain)) return null;
	if (SKIPPED_DOMAINS.some(skipped => domain === skipped || domain.endsWith('.' + skipped))) return null;
	return domain;
};

// True when both URLs resolve to the same matchable company domain — i.e. the
// user navigated within one site rather than to a different website. Used to
// keep the current suggestions on screen (and refresh in the background) instead
// of flashing the loader on same-site navigation. Non-matchable pages (the CRM,
// search engines, webmail) are never "the same website" as anything.
export const isSameWebsite = (a: string, b: string): boolean => {
	const domainA = matchablePageDomain(a);
	return domainA !== null && domainA === matchablePageDomain(b);
};

// The profile a LinkedIn personal profile page shows (`/in/<handle>`), the same for its sub-pages
// (contact info, activity), so they count as one person (WEB-15175). Company pages,
// Sales Navigator and people-search are intentionally excluded (out of scope).
export const linkedInProfilePath = (url: string): string | null => {
	if (!url) return null;
	try {
		const { hostname, pathname } = new URL(url);
		const host = hostname.replace(/^www\./i, '').toLowerCase();
		if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return null;
		return pathname.match(/^\/in\/[^/]+/)?.[0] ?? null;
	} catch {
		return null;
	}
};

export const isLinkedInProfile = (url: string): boolean => linkedInProfilePath(url) !== null;

// LinkedIn company pages (linkedin.com/company/<slug>, any sub-page): "People at this company"
// is offered there (WEB-13375).
export const isLinkedInCompanyPage = (url: string): boolean => {
	if (!url) return false;
	try {
		const { hostname, pathname } = new URL(url);
		const host = hostname.replace(/^www\./i, '').toLowerCase();
		return (host === 'linkedin.com' || host.endsWith('.linkedin.com')) && /^\/company\/[^/]+/.test(pathname);
	} catch {
		return false;
	}
};

// Best-effort display name from a LinkedIn profile <title> for the ambient tab
// hint only — the authoritative name comes from the server extract. Titles look
// like "(3) Johan Lindblom - Sales Manager - Bricmate AB | LinkedIn".
export const parseLinkedInName = (title: string): string => {
	if (!title) return '';
	let t = title.replace(/\s*\|\s*LinkedIn\s*$/i, '').trim();
	if (!t || /^LinkedIn$/i.test(t)) return '';
	t = t.replace(/^\(\d+\)\s*/, '');
	const dash = t.indexOf(' - ');
	if (dash > 0) t = t.slice(0, dash);
	return t.trim();
};
